/**
 * The shared kit delivery rule (M9 CH-5a; extracted from the M7 restart kit, plan S15 and S40, economy plan M7-4
 * and M7-7). Record-agnostic: it knows a requirement and what has been delivered so far, never whose record holds
 * them, so M7 restart kits (`story/reactivation/kit.ts`) and M9 hulk kits share one rule.
 *
 * - **Kits are goods and salvaged components** (M9 plan §2, SY-1/D-362). A requirement names `GoodId`s only, so a
 *   raw ware (tetherstone included) can never be in one, and a delivery never touches `cargo.wares`.
 * - **Components** count only at `salvage.json bands.serviceable` or better (never Wrecked), are consumed whole and
 *   only while their type is still missing.
 * - **Goods** are taken in whole units, never more than is missing; the hold loses them through `withGood`.
 * - **Cargo leaves the hold only for what was taken** (`deliverGoodsAndParts`).
 * - **Rush-pattern bonus** (M8 MC-h4b, U14): optional (`rushFuelCellsPerPart`, 0 by default). A Rush-pattern
 *   component that fills a missing slot credits that many fuel cells, never more than are still missing.
 *
 * Career in, Career out; pure. UI-rate, never per frame.
 */
import { withGood, withoutComponent } from '../career/ops'
import { goodT, type GoodId } from '../career/parts'
import type { Career } from '../career/types'
import { COMPONENT_TYPES, type ComponentType, type SalvageFileRaw } from '../data/m2Schemas'
import salvageJson from '../data/salvage.json'
import { conditionBand, CONDITION_BAND } from '../salvage/generate'

export type SalvageBands = Readonly<SalvageFileRaw['bands']>

/** The shipped condition bands (`salvage.json bands`, SY-1). */
export const SALVAGE_BANDS: SalvageBands = Object.freeze({
  ...salvageJson.bands,
})

/** What a kit needs (salvaged components and goods). */
export interface KitRequirement {
  components: Partial<Record<ComponentType, number>>
  goods: Partial<Record<GoodId, number>>
}

/** What has been delivered toward a kit so far (the shape a record stores, e.g. M7's `ReactivationKit`). */
export interface KitProgress {
  components: Partial<Record<ComponentType, number>>
  goods: Record<string, number>
}

/** One delivery: a salvaged component (whole) or a quantity of a good. */
export type KitItem =
  | {
      kind: 'component'
      type: ComponentType
      condition: number
      /** A Rush-pattern part (D-502) credits `rushFuelCellsPerPart` fuel cells (MC-h4b). */
      provenance?: 'rush'
    }
  | { kind: 'good'; good: GoodId; qty: number }

export type KitItemRefusal = 'wrecked' | 'notNeeded'

export interface KitDeliverOptions {
  bands?: SalvageBands
  /** Fuel cells a Rush-pattern component credits (0: no bonus). */
  rushFuelCellsPerPart?: number
}

export type KitItemResult =
  | {
      ok: true
      delivered: KitProgress
      /** Units taken (a good delivery takes at most what is missing). */
      taken: number
      /** Fuel cells a Rush-pattern component credited (0 otherwise). */
      credited: number
    }
  | { ok: false; reason: KitItemRefusal }

/** The hold items a hold delivery took. */
export interface KitTaken {
  components: { id: string; type: ComponentType }[]
  goods: Partial<Record<GoodId, number>>
}

export interface KitHoldDelivery {
  career: Career
  delivered: KitProgress
  taken: KitTaken
  /** Fuel cells Rush-pattern components credited. */
  credited: number
}

const emptyProgress = (): KitProgress => ({ components: {}, goods: {} })

/** A salvaged component counts toward a kit only at Serviceable or Intact condition (Rush-pattern or not). */
export function acceptsComponent(condition: number, bands: SalvageBands = SALVAGE_BANDS): boolean {
  return conditionBand(condition, bands) !== CONDITION_BAND.wrecked
}

/** The fuel cells a Rush-pattern component credits to a kit that still misses `missingCells` (U14 bonus). */
export function rushCellCredit(provenance: 'rush' | undefined, missingCells: number, perPart: number): number {
  if (provenance !== 'rush') return 0
  return Math.max(0, Math.min(perPart, missingCells))
}

/** What is still missing (zero entries left out). */
export function kitMissing(req: KitRequirement, delivered: KitProgress | undefined): KitRequirement {
  const have = delivered ?? emptyProgress()
  const components: Partial<Record<ComponentType, number>> = {}
  for (const t of COMPONENT_TYPES) {
    const need = (req.components[t] ?? 0) - (have.components[t] ?? 0)
    if (need > 0) components[t] = need
  }
  const goods: Partial<Record<GoodId, number>> = {}
  for (const [g, n] of Object.entries(req.goods) as [GoodId, number | undefined][]) {
    const need = (n ?? 0) - (have.goods[g] ?? 0)
    if (need > 0) goods[g] = need
  }
  return { components, goods }
}

/** Every component and good delivered. */
export function kitComplete(req: KitRequirement, delivered: KitProgress | undefined): boolean {
  const m = kitMissing(req, delivered)
  return Object.keys(m.components).length === 0 && Object.keys(m.goods).length === 0
}

/**
 * Applies one delivery to `delivered` (the hold is not touched): a component below Serviceable is refused
 * (`wrecked`), as is anything the kit no longer needs (`notNeeded`).
 */
export function deliverItem(
  req: KitRequirement,
  delivered: KitProgress | undefined,
  item: KitItem,
  opts: KitDeliverOptions = {},
): KitItemResult {
  const missing = kitMissing(req, delivered)
  const kit = delivered ?? emptyProgress()
  if (item.kind === 'component') {
    if (!acceptsComponent(item.condition, opts.bands)) return { ok: false, reason: 'wrecked' }
    if ((missing.components[item.type] ?? 0) <= 0) return { ok: false, reason: 'notNeeded' }
    const credited = rushCellCredit(item.provenance, missing.goods.fuel_cells ?? 0, opts.rushFuelCellsPerPart ?? 0)
    const next: KitProgress = {
      ...kit,
      components: {
        ...kit.components,
        [item.type]: (kit.components[item.type] ?? 0) + 1,
      },
    }
    if (credited > 0) next.goods = { ...kit.goods, fuel_cells: (kit.goods.fuel_cells ?? 0) + credited }
    return { ok: true, delivered: next, taken: 1, credited }
  }
  const taken = Math.min(Math.max(0, Math.floor(item.qty)), missing.goods[item.good] ?? 0)
  if (taken <= 0) return { ok: false, reason: 'notNeeded' }
  const next: KitProgress = {
    ...kit,
    goods: {
      ...kit.goods,
      [item.good]: (kit.goods[item.good] ?? 0) + taken,
    },
  }
  return { ok: true, delivered: next, taken, credited: 0 }
}

/**
 * Delivers everything the kit still needs from the hold: every kit-ready component whose type is still missing
 * (Rush-pattern parts first, so their fuel-cell bonus lands; the rest in hold order), then every missing good in
 * whole units, as much as the hold carries. The hold loses only what was taken. Nothing taken → `taken` is empty
 * and `career` is the input.
 */
export function deliverGoodsAndParts(
  req: KitRequirement,
  delivered: KitProgress | undefined,
  career: Career,
  opts: KitDeliverOptions = {},
): KitHoldDelivery {
  const taken: KitTaken = { components: [], goods: {} }
  let next = career
  let kit = delivered ?? emptyProgress()
  let credited = 0
  const rushFirst = [...career.cargo.components].sort((a, b) => Number(b.provenance === 'rush') - Number(a.provenance === 'rush'))
  for (const c of rushFirst) {
    const item: KitItem = { kind: 'component', type: c.type, condition: c.condition }
    if (c.provenance !== undefined) item.provenance = c.provenance
    const r = deliverItem(req, kit, item, opts)
    if (!r.ok) continue
    const removed = withoutComponent(next, c.id)
    if (removed === next) continue
    next = removed
    kit = r.delivered
    credited += r.credited
    taken.components.push({ id: c.id, type: c.type })
  }
  for (const [g, need] of Object.entries(kitMissing(req, kit).goods) as [GoodId, number][]) {
    const have = Math.floor(goodT(next.cargo.parts, g) + 1e-9)
    if (have <= 0 || need <= 0) continue
    const r = deliverItem(req, kit, { kind: 'good', good: g, qty: have }, opts)
    if (!r.ok) continue
    next = withGood(next, g, -r.taken)
    kit = r.delivered
    taken.goods[g] = r.taken
  }
  return { career: next, delivered: kit, taken, credited }
}
